import {
  Input,
  Output,
  OnChanges,
  Component,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { endOfMinute, startOfMinute } from 'date-fns';

import { SuiDateRange } from '@sensika/sui/date-picker';

import {
  isQuickPeriodValue,
  quickPeriodRange,
  DEFAULT_TIME_PRESETS,
  QUICK_PERIOD_TOOLTIP_KEYS,
  QuickPeriodValue,
  TimePresetOption,
} from './date-range-selection.presets';

// * Presentational range panel: preset bar + inline range calendar + time
// * pickers. Store-free by design (WAP-1590) - context-specific concerns
// * (period history, company date bounds) are supplied by the consumer via
// * the `range-panel-extras` content slot and the minDate/maxDate inputs.
@Component({
  selector: 'app-date-range-selection',
  templateUrl: './date-range-selection.component.html',
  styleUrls: ['./date-range-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangeSelectionComponent implements OnChanges {
  @Input() fromDate: Date;
  @Input() toDate: Date;
  @Input() presetBar: boolean = true;
  /** Optional second preset row with now-relative minute/hour durations (grid filters opt in). */
  @Input() timePresetBar: boolean = false;
  /** The preset behind the bound range, so its button is highlighted when the panel opens (WAP-1763); applied after the date sync in ngOnChanges. */
  @Input() activePreset: string | null = null;
  @Input() timeDetails: boolean = true;
  /** Compact calendars (26px day squares) for views that must fit a 1080px viewport. */
  @Input() compact: boolean = false;
  /** Calendar bounds; consumers typically bind selectCompanyDateBounds. */
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  /** The applied range, plus the quick or time preset it came from (`null` for a hand-picked range). */
  @Output() dateRangeSelection = new EventEmitter<{
    from: Date;
    to: Date;
    preset: string | null;
  }>();

  /** Day-level value fed to the inline range calendar (full datetime lives on fromDate/toDate). */
  rangeValue: SuiDateRange = { start: null, end: null };
  /** Pin the calendar to [previous month, current month] (current on the right). */
  readonly initialMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 1,
    1,
  );
  // Active quick-period option value ('1Y' … '24H' | 'today', or a time preset
  // '5m' … '12h'), or null when the current range is custom (no preset
  // highlighted). Both preset rows share it - a value only ever exists in one
  // row, so selecting in either row clears the highlight in the other.
  // Matched by value so it works regardless of the (translated) label.
  activePeriod: string | null = null;

  dateRangePeriodsBase = [
    { label: '1Y', value: '1Y' },
    { label: '6M', value: '6M' },
    { label: '3M', value: '3M' },
    { label: '1M', value: '1M' },
    { label: '2W', value: '2W' },
    { label: '1W', value: '1W' },
    { label: '3D', value: '3D' },
    { label: '24H', value: '24H' },
    {
      label: this.translateService.instant('date-range-selection.label.today'),
      value: 'today',
    },
  ];
  /** Section label above the preset rows. */
  get presetsLabel(): string {
    return this.translateService.instant(
      'date-range-selection.label.date-time-presets',
    );
  }

  /** The calendar preset row with its tooltips. */
  get dateRangePeriods(): Array<{
    label: string;
    value: string;
    tooltip: string;
  }> {
    return this.dateRangePeriodsBase.map((option) => ({
      ...option,
      tooltip: this.translateService.instant(
        QUICK_PERIOD_TOOLTIP_KEYS[option.value as QuickPeriodValue],
      ),
    }));
  }

  /** The time preset row with its tooltips. */
  get timePresetOptions(): Array<{
    label: string;
    value: string;
    tooltip: string;
  }> {
    return this.timePresets.map((option) => ({
      label: option.label,
      value: option.value,
      tooltip: this.translateService.instant(option.tooltipKey),
    }));
  }

  // Now-relative durations for the optional second row. Lowercase on purpose:
  // uppercase in the row above means calendar periods ('1M' = month), lowercase
  // here means clock durations from now ('5m' = minutes) - Grafana convention.
  /** The time preset row; consumers narrow or widen the list (a wall display wants 5m .. 12h). */
  @Input() timePresets: TimePresetOption[] = DEFAULT_TIME_PRESETS;

  constructor(private translateService: TranslateService) {}

  /** The range last emitted, so an echo of it from the consumer is recognised. */
  private lastEmitted: { from: number | null; to: number | null } | null = null;

  ngOnChanges(changes: SimpleChanges) {
    // Reflect the bound range into the calendar highlight. A range that
    // differs from the panel's own last emission came from outside (e.g. a
    // history period) - drop the preset highlight and re-sync the calendar.
    // An echo of the panel's own emission (round-tripped by the consumer)
    // must leave the calendar alone: after the first click it holds a start
    // with the end still pending, and syncing the emitted one-day range into
    // it would turn the next click into a fresh range instead of the end.
    if (changes['fromDate'] || changes['toDate']) {
      if (this.isEchoOfOwnEmission()) {
        return;
      }
      this.activePeriod = null;
      this.syncRangeValue();
    }
    // The consumer's preset wins over the date sync above: an inherited preset
    // opens highlighted, a history pick (preset null) opens plain.
    if (changes['activePreset']) {
      this.activePeriod = this.activePreset ?? null;
    }
  }

  private isEchoOfOwnEmission(): boolean {
    return (
      !!this.lastEmitted &&
      this.lastEmitted.from === (this.fromDate?.getTime() ?? null) &&
      this.lastEmitted.to === (this.toDate?.getTime() ?? null)
    );
  }

  private emit() {
    this.lastEmitted = {
      from: this.fromDate?.getTime() ?? null,
      to: this.toDate?.getTime() ?? null,
    };
    this.dateRangeSelection.emit({
      from: this.fromDate,
      to: this.toDate,
      preset: this.activePeriod,
    });
  }

  // * Inline range calendar selection. Picking days resets the time to
  // * 00:00 / 23:59:59 (the time pickers below refine it). The first click is
  // * already a complete one-day range and is emitted as such, so what the
  // * inputs show is what Apply applies (WAP-1463); the calendar keeps waiting
  // * for the second click (see ngOnChanges), which widens the range - the
  // * primitive orders an earlier second day start-first, the swap below only
  // * guards against a reversed pair reaching this panel some other way.
  onRangeChange(range: SuiDateRange): void {
    this.activePeriod = null;
    let start = range?.start ?? null;
    let end = range?.end ?? null;
    if (start && end && end < start) {
      [start, end] = [end, start];
    }
    this.rangeValue = { start, end };
    if (!start) {
      return;
    }

    this.fromDate = start;
    this.toDate = this.endOfDay(end ?? start);
    this.normalizeRange();
    this.emit();
  }

  // * Seconds are never part of a range: the range travels into query cache
  // * keys (the memcached layer in front of Solr), and 'now'-seeded values
  // * (24H preset, time-picker seeds) would make every selection a unique key.
  // * Normalized to the established inclusive convention: start :00, end :59.
  private normalizeRange(): void {
    if (this.fromDate) {
      this.fromDate = startOfMinute(this.fromDate);
    }
    if (this.toDate) {
      this.toDate = endOfMinute(this.toDate);
    }
  }

  /** Helper: push a Date to 23:59:59.0 */
  private endOfDay(d: Date): Date {
    const e = new Date(d);
    e.setHours(23, 59, 59, 0);
    return e;
  }

  /** Mirror fromDate/toDate into the inline range calendar (day part only). */
  private syncRangeValue(): void {
    this.rangeValue = {
      start: this.fromDate ?? null,
      end: this.toDate ?? null,
    };
  }

  setQuickPeriod(period: string) {
    // Unknown period
    if (!isQuickPeriodValue(period)) return;

    // Range computation lives in date-range-selection.presets.ts so consumers
    // can resolve a named preset without the component; already normalized.
    const { from, to } = quickPeriodRange(period);
    this.fromDate = from;
    this.toDate = to;

    this.activePeriod = period;
    this.syncRangeValue();
    this.emit();
  }

  // * Time presets are now-relative ("last N minutes/hours"), unlike the
  // * calendar presets above which anchor to today. normalizeRange() then
  // * clamps the live seconds to the :00/:59 convention - the emitted range
  // * must never carry them (memcached keys, see normalizeRange).
  setQuickTimePeriod(period: string) {
    const minutes = this.timePresets.find(
      (option) => option.value === period,
    )?.minutes;
    if (!minutes) return;

    this.toDate = new Date();
    this.fromDate = new Date(this.toDate.getTime() - minutes * 60 * 1000);

    this.normalizeRange();
    this.activePeriod = period;
    this.syncRangeValue();
    this.emit();
  }

  onFromDateChange(newDate: Date) {
    // A field edit may change the day → keep the calendar highlight in sync.
    this.normalizeRange();
    this.syncRangeValue();
    this.emit();
  }
}
